import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { userVerificationsApi, VerificationDetail, SubmitVerificationData } from 'shared/services/userVerificationsApi';
import { fileService } from 'shared/services/fileService';

interface VerificationSubmitDialogProps {
  open: boolean;
  onClose: () => void;
  verification: VerificationDetail;
  companyId: string;
  companyName: string;
  onSubmitted: () => void;
  onShowMessage?: (message: string, severity: 'success' | 'error') => void;
}

export const VerificationSubmitDialog: React.FC<VerificationSubmitDialogProps> = ({
  open,
  onClose,
  verification,
  companyId,
  companyName,
  onSubmitted,
  onShowMessage
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentoId, setDocumentoId] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<SubmitVerificationData>({
    documentUrl: verification.documentUrl || '',
    certificateNumber: verification.certificateNumber || '',
    expiryDate: verification.expiryDate || '',
    notes: ''
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setLocalError('El archivo no puede superar los 10MB');
        return;
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setLocalError('Solo se permiten archivos PDF, JPG, JPEG o PNG');
        return;
      }

      setSelectedFile(file);
      setLocalError(null);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setLocalError(null);

      let fileId = documentoId;

      // If there's a selected file but no document ID, upload the file first
      if (selectedFile && !fileId) {
        try {
          const uploadResponse = await fileService.uploadFile(selectedFile);

          if (!uploadResponse.success) {
            throw new Error(uploadResponse.message || 'Error al subir el archivo');
          }

          fileId = uploadResponse.fileId;
          setDocumentoId(fileId);
        } catch (uploadError: any) {
          const errorMsg = uploadError.message || 'Error al subir el archivo';
          setLocalError(errorMsg);
          if (onShowMessage) {
            onShowMessage(errorMsg, 'error');
          }
          setLoading(false);
          return;
        }
      }

      // Validate required fields
      if (!fileId) {
        const errorMsg = 'Debe seleccionar un documento';
        setLocalError(errorMsg);
        if (onShowMessage) {
          onShowMessage(errorMsg, 'error');
        }
        setLoading(false);
        return;
      }

      if (verification.validityPeriod && !formData.expiryDate) {
        const errorMsg = 'Debe especificar la fecha de vencimiento';
        setLocalError(errorMsg);
        if (onShowMessage) {
          onShowMessage(errorMsg, 'error');
        }
        setLoading(false);
        return;
      }

      // Build submit data with documentoId instead of documentUrl
      const submitData: any = {
        documentoId: fileId,
        certificateNumber: formData.certificateNumber,
        expiryDate: formData.expiryDate,
        notes: formData.notes
      };

      await userVerificationsApi.submitVerification(
        companyId,
        verification.id,
        submitData
      );

      // Show success message via parent Snackbar
      if (onShowMessage) {
        onShowMessage('Verificación enviada exitosamente', 'success');
      }

      // Close dialog and refresh
      onSubmitted();
      onClose();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Error al enviar la verificación';
      setLocalError(errorMsg);
      if (onShowMessage) {
        onShowMessage(errorMsg, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = () => {
    switch (verification.status) {
      case 'approved':
        return { color: 'success', label: 'Aprobado' };
      case 'rejected':
        return { color: 'error', label: 'Rechazado' };
      case 'pending':
      case 'in_review':
        return { color: 'warning', label: 'En revisión' };
      case 'expired':
        return { color: 'error', label: 'Expirado' };
      default:
        return { color: 'default', label: 'No enviado' };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="span">
              {verification.status === 'not_submitted' ? 'Enviar' : 'Actualizar'} Verificación
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {companyName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {verification.name}
            </Typography>
            <Chip 
              label={statusInfo.label}
              color={statusInfo.color as any}
              size="small"
            />
          </Box>
          
          {verification.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {verification.description}
            </Typography>
          )}
          
          {verification.rejectionReason && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Razón de rechazo:</strong> {verification.rejectionReason}
              </Typography>
            </Alert>
          )}
        </Box>

        {localError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
            {localError}
          </Alert>
        )}

        {/* Document Upload Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Documento *
          </Typography>
          
          {documentoId ? (
            <Box sx={{ 
              p: 2, 
              border: '1px solid',
              borderColor: 'success.main',
              borderRadius: 1,
              bgcolor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="body2">
                  Documento cargado
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                onClick={() => {
                  setDocumentoId('');
                  setSelectedFile(null);
                }}
              >
                Cambiar
              </Button>
            </Box>
          ) : (
            <Box>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept=".pdf,.jpg,.jpeg,.png"
                aria-label="Subir archivo"
              />
              
              {selectedFile ? (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ 
                    p: 2, 
                    border: '1px dashed',
                    borderColor: 'success.main',
                    borderRadius: 1,
                    bgcolor: 'success.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachFileIcon color="success" />
                      <Typography variant="body2">
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </Typography>
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={() => setSelectedFile(null)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={<CloudUploadIcon />}
                  sx={{ py: 2 }}
                >
                  Seleccionar Archivo
                </Button>
              )}
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Formatos permitidos: PDF, JPG, PNG. Tamaño máximo: 10MB
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Certificate Number (optional) */}
        {(verification.type === 'certification' || verification.type === 'course') && (
          <TextField
            fullWidth
            label="Número de Certificado"
            value={formData.certificateNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, certificateNumber: e.target.value }))}
            sx={{ mb: 3 }}
          />
        )}
        
        {/* Expiry Date */}
        {verification.validityPeriod && (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DatePicker
              label="Fecha de Vencimiento *"
              value={formData.expiryDate ? new Date(formData.expiryDate) : null}
              onChange={(date) => {
                if (date) {
                  setFormData(prev => ({ ...prev, expiryDate: date.toISOString() }));
                }
              }}
              minDate={new Date()}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  sx: { mb: 3 }
                }
              }}
            />
          </LocalizationProvider>
        )}
        
        {/* Notes */}
        <TextField
          fullWidth
          label="Notas adicionales"
          multiline
          rows={3}
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          helperText="Información adicional relevante para esta verificación"
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (!documentoId && !selectedFile) || (!!verification.validityPeriod && !formData.expiryDate)}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? 'Enviando...' : 'Enviar Verificación'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};